import { SetMirrorData } from '@type/job-add';
import { useTranslation } from 'next-i18next';
import { Modal } from '@components/common';
import { Button } from '@components/common/Forms';
import { useSwrWorkplaceDetailByName } from '@hooks/useSwrApi/endpoints/workplace';
import { useState, Dispatch, SetStateAction } from 'react';
import { SimpleEmployerProfileList } from '@components/templates/job-add/edit/functions/jobCompanyNameFunctions';
import { SearchEmployer } from '@components/templates/job-add/edit/form/partials/SearchEmployer';

/** Props */
interface SearchEmployerModalProps {
  locale: Locale,
  isOpen: boolean,
  toggleModal: () => void,
  title?: string,
  labelDescriptionClass?: string,
  setFormValues?: Dispatch<SetStateAction<object>>,
  inputNameForSet?: string,
  setEmployerList?: Dispatch<SetStateAction<SimpleEmployerProfileList[] | undefined>>,
  setMirrorData?: SetMirrorData,
}

/**
 * @description Search Employer modal component.
 * - `locale` - the Locale
 * - `isOpen` - boolean, the modal state
 * - `toggleModal` - function, the modal toggle function
 * - `title` - string, the modal title
 * - `labelDescriptionClass` - string, additional classes for the label description
 * - `setFormValues` - function, set form values from useBjForm
 * - `inputNameForSet` - string, the name of the input field to set
 * - `setEmployerList` - function, set the employer list from useBjForm
 * - `setMirrorData` - function, set mirror data from useBjForm
 */
export const SearchEmployerModal = (props: SearchEmployerModalProps) => {
  // Destructure props
  const {
    locale,
    isOpen,
    toggleModal,
    title,
    labelDescriptionClass = '',
    setFormValues,
    inputNameForSet = 'companyName',
    setEmployerList,
    setMirrorData,
  } = props;

  // Translation
  const { t } = useTranslation();

  // State for searched / selected value
  const [workplaceName, setWorkplaceName] = useState<string>('');

  // Add new workplace. We mutate the employer list predefined object with the new workplace name.
  // Also, we set the form value with the new workplace name.
  const addNewWorkplace = () => {
    setEmployerList?.((prev) => prev?.map(
      (item) => {
        if (item?.fromSearch) {
          return { ...item, name: workplaceName, radioValue: workplaceName };
        }
        return item;
      },
    ));

    setTimeout(() => {
      setFormValues?.({ [inputNameForSet]: workplaceName });
      setMirrorData?.({ [inputNameForSet]: workplaceName });
      toggleModal();
    }, 50);
  };

  // SWR: Get workplace detail by name
  // **************************
  const {
    trigger,
    isMutating,
  } = useSwrWorkplaceDetailByName(
    locale,
    // Success callback.
    () => {
      addNewWorkplace();
    },
    // Error callback.
    (errorResponse) => {
      if (errorResponse?.errors?.[0]?.code === 404) addNewWorkplace();
    },
  );

  // Render component
  // **************************
  return (
    <Modal
      isOpen={isOpen}
      onClose={toggleModal}
      size="sm"
      header={t(title ?? 'job.add.label.employer.other')}
      className="!overflow-visible"
    >
      <Modal.Body
        className="bg-surface-50"
      >
        <SearchEmployer
          labelDescriptionClass={labelDescriptionClass}
          setWorkplaceName={setWorkplaceName}
          isMutating={isMutating}
          fieldName={inputNameForSet}
          className="!-mt-1.5"
        />
      </Modal.Body>

      <Modal.Footer
        className="text-right"
      >
        <Button
          size="sm"
          color="secondary"
          isLoading={isMutating}
          disabled={workplaceName.length === 0}
          onClick={() => {
            void trigger({ workplaceName });
          }}
        >
          {t('global.action.add')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
